import React, { useState, useEffect } from 'react';
import { 
  Eye, 
  Trash2, 
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Heart,
  AlertCircle
} from 'lucide-react';
import { getCompatibilityOrders, deleteUserDetail, updateLoveCompatibilityOrder, searchCompatibilityOrders, getPaymentStatus } from '../services/api';
import { getCompatibilityAnalytics, getTodayDate, getYesterdayDate } from '../services/analytics';
import { LoveCompatibilityOrder, FilterParams } from '../types';
import { format } from 'date-fns';
import Pagination from './Pagination';
import EnhancedSearch, { SearchField } from './EnhancedSearch';

const LoveCompatibility: React.FC = () => {
  const [orders, setOrders] = useState<LoveCompatibilityOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<LoveCompatibilityOrder | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState<LoveCompatibilityOrder | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<any>(null);
  const [filters, setFilters] = useState<FilterParams>({
    limit: 20,
    offset: 0,
    sort_by: 'created_at',
    sort_order: 'DESC'
  });
  const [totalOrders, setTotalOrders] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState<any>(null);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [analytics, setAnalytics] = useState<any>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(getTodayDate());

  // Enhanced search handler
  const handleEnhancedSearch = (term: string, field: SearchField) => {
    if (term.trim()) {
      setIsSearchMode(true);
      setSearchTerm(term);
      
      // Use enhanced search API based on selected field
      searchCompatibilityOrders(term, filters).then(response => {
        if (response.data && Array.isArray(response.data)) {
          setOrders(response.data);
          setTotalOrders(response.pagination?.total || response.data.length);
          setPagination(response.pagination);
        }
      }).catch(error => {
        console.error('Search error:', error);
        setError('Search failed. Please try again.');
        setOrders([]);
        setTotalOrders(0);
        setPagination(null);
      });
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [filters]);

  useEffect(() => {
    fetchAnalytics(selectedDate);
  }, [selectedDate]);

  const fetchAnalytics = async (date: string) => {
    try {
      setAnalyticsLoading(true);
      const response = await getCompatibilityAnalytics(date);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setAnalytics(null);
    } finally {
      setAnalyticsLoading(false);
    }
  };



  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching orders with filters:', filters);
      const response = await getCompatibilityOrders({
        ...filters,
        limit: filters.limit
      });
      
      if (response.data && Array.isArray(response.data)) {
        setOrders(response.data);
        setTotalOrders(response.pagination?.total || response.data.length);
        setPagination(response.pagination);
      } else {
        setOrders([]);
        setTotalOrders(0);
        setPagination(null);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to fetch orders. Please try again.');
      setOrders([]);
      setTotalOrders(0);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (orderId: string) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      try {
        await deleteUserDetail(orderId);
        setOrders(orders.filter(order => order.order_id !== orderId));
        alert('Order deleted successfully');
      } catch (error) {
        console.error('Error deleting order:', error);
        alert('Failed to delete order');
      }
    }
  };

  const handleViewDetails = async (order: LoveCompatibilityOrder) => {
    setSelectedOrder(order);
    setShowModal(true);
    
    try {
      const status = await getPaymentStatus(order.order_id);
      setPaymentStatus(status);
    } catch (error) {
      console.error('Error fetching payment status:', error);
      setPaymentStatus(null);
    }
  };

  const handleEdit = (order: LoveCompatibilityOrder) => {
    setEditingOrder({ ...order });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingOrder) return;
    
    try {
      await updateLoveCompatibilityOrder(editingOrder.order_id, editingOrder);
      setOrders(orders.map(order => 
        order.order_id === editingOrder.order_id ? editingOrder : order
      ));
      setShowEditModal(false);
      setEditingOrder(null);
      alert('Order updated successfully');
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Failed to update order');
    }
  };

  const handleFilterChange = (key: keyof FilterParams, value: any) => {
    console.log('Filter change:', key, value);
    setFilters(prev => ({ ...prev, [key]: value, offset: 0 }));
  };

  const handleClearFilters = () => {
    setFilters({
      limit: 20,
      offset: 0,
      sort_by: 'created_at',
      sort_order: 'DESC'
    });
    setSearchTerm('');
    setIsSearchMode(false);
    fetchOrders();
  };

  const handlePageChange = (newOffset: number) => {
    setFilters(prev => ({ ...prev, offset: newOffset }));
  };

  const totalPages = Math.ceil(totalOrders / filters.limit);
  const currentPage = Math.floor(filters.offset / filters.limit) + 1;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Love Compatibility Orders</h2>
        <button
          onClick={fetchOrders}
          className="btn-outline flex items-center space-x-2"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Analytics Section */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Daily Analytics</h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setSelectedDate(getTodayDate())}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                selectedDate === getTodayDate()
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => setSelectedDate(getYesterdayDate())}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                selectedDate === getYesterdayDate()
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Yesterday
            </button>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {analyticsLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : analytics ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="text-sm text-blue-600 font-medium">Total Orders</div>
              <div className="text-2xl font-bold text-blue-900">{analytics.total_orders}</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="text-sm text-green-600 font-medium">Total Amount</div>
              <div className="text-2xl font-bold text-green-900">₹{analytics.total_amount.toLocaleString()}</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <div className="text-sm text-purple-600 font-medium">Completed Orders</div>
              <div className="text-2xl font-bold text-purple-900">{analytics.completed_orders}</div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <div className="text-sm text-orange-600 font-medium">Completion Rate</div>
              <div className="text-2xl font-bold text-orange-900">{analytics.completion_rate}%</div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No analytics data available for the selected date.
          </div>
        )}
      </div>

      {/* Search Results Indicator */}
      {isSearchMode && searchTerm && (
        <div className="card border-blue-200 bg-blue-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-blue-800">
              <span className="font-medium">
                Search Results for "{searchTerm}" - {totalOrders} orders found
              </span>
            </div>
            <button
              onClick={handleClearFilters}
              className="text-blue-600 hover:text-blue-800 underline text-sm"
            >
              Clear Search
            </button>
          </div>
        </div>
      )}

      {/* Enhanced Search */}
      <div className="card">
        <div className="mb-4">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Search Orders</h3>
          <EnhancedSearch
            onSearch={handleEnhancedSearch}
            placeholder="Search by name, email, phone, amount, or order ID..."
            className="w-full"
          />
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="card border-red-200 bg-red-50">
          <div className="flex items-center space-x-2 text-red-800">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">{error}</span>
          </div>
        </div>
      )}

             {/* Filters */}
       <div className="card">
         <div className="flex justify-between items-center mb-4">
           <h3 className="text-lg font-medium text-gray-900">Filters & Search</h3>
           <button
             onClick={handleClearFilters}
             className="text-sm text-gray-600 hover:text-gray-800 underline"
           >
             Clear All Filters
           </button>
         </div>
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Search by name, email, phone, amount, or order ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && setSearchTerm((e.target as HTMLInputElement).value)}
                className="input-field pr-10"
              />
              <div className="absolute right-2 top-2 text-gray-400">
                <span className="text-sm">🔍</span>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
              className="input-field"
            >
              <option value="">All Status</option>
              <option value="INITIATED">Initiated</option>
              <option value="PENDING">Pending</option>
              <option value="SUCCESS">Success</option>
              <option value="Completed">Completed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
            <select
              value={filters.sort_by}
              onChange={(e) => handleFilterChange('sort_by', e.target.value)}
              className="input-field"
            >
              <option value="created_at">Date</option>
              <option value="customer1_name">Name</option>
              <option value="order_amount">Amount</option>
              <option value="status">Status</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sort Order</label>
            <select
              value={filters.sort_order}
              onChange={(e) => handleFilterChange('sort_order', e.target.value as 'ASC' | 'DESC')}
              className="input-field"
            >
              <option value="DESC">Descending</option>
              <option value="ASC">Ascending</option>
            </select>
          </div>
        </div>
      </div>

              {/* Orders Table */}
        <div className="card">
          {!Array.isArray(orders) || orders.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <Heart className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Orders Found</h3>
              <p className="text-gray-500">
                {!Array.isArray(orders) 
                  ? 'Unable to load orders. Please check your connection and try again.'
                  : 'No love compatibility orders match your current filters.'
                }
              </p>
              <button
                onClick={fetchOrders}
                className="mt-4 btn-primary"
              >
                Try Again
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="table-header">Order ID</th>
                    <th className="table-header">Customer 1</th>
                    <th className="table-header">Customer 2</th>
                    <th className="table-header">Contact</th>
                    <th className="table-header">Location</th>
                    <th className="table-header">Amount</th>
                    <th className="table-header">Status</th>
                    <th className="table-header">Date</th>
                    <th className="table-header">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => (
                    <tr key={order.order_id} className="hover:bg-gray-50">
                      <td className="table-cell font-mono text-sm">{order.order_id}</td>
                      <td className="table-cell">
                        <div>
                          <div className="font-medium text-gray-900">{order.customer1_name}</div>
                          {order.customer1_gender && (
                            <div className="text-sm text-gray-500 capitalize">{order.customer1_gender}</div>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        {order.customer2_name ? (
                          <div>
                            <div className="font-medium text-gray-900">{order.customer2_name}</div>
                            {order.customer2_gender && (
                              <div className="text-sm text-gray-500 capitalize">{order.customer2_gender}</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">Single</span>
                        )}
                      </td>
                      <td className="table-cell">
                        <div>
                          <div className="text-sm text-gray-900">{order.customer_email}</div>
                          {order.customer_phone && (
                            <div className="text-sm text-gray-500">{order.customer_phone}</div>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div>
                          <div className="text-sm text-gray-900">{order.customer1_city}</div>
                          <div className="text-sm text-gray-500">{order.customer1_state}</div>
                        </div>
                      </td>
                      <td className="table-cell font-medium">₹{order.order_amount}</td>
                      <td className="table-cell">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          order.status === 'Completed' || order.status === 'SUCCESS'
                            ? 'bg-green-100 text-green-800'
                            : order.status === 'INITIATED' || order.status === 'PENDING'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="table-cell text-sm text-gray-500">
                        {format(new Date(order.created_at.replace('Z', '')), 'MMM dd, yyyy, hh:mm a')}
                      </td>
                      <td className="table-cell">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewDetails(order)}
                            className="text-secondary-600 hover:text-secondary-900"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(order)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit Order"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(order.order_id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Order"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        {/* Pagination */}
        {Array.isArray(orders) && orders.length > 0 && totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-gray-700">
              Showing {filters.offset + 1} to {Math.min(filters.offset + filters.limit, totalOrders)} of {totalOrders} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(filters.offset - filters.limit)}
                disabled={filters.offset === 0}
                className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <span className="px-3 py-2 text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(filters.offset + filters.limit)}
                disabled={filters.offset + filters.limit >= totalOrders}
                className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {showModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Compatibility Order Details</h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              <div className="space-y-6">
                {/* Order Information */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Order ID</label>
                    <p className="text-sm text-gray-900 font-mono">{selectedOrder.order_id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Customer ID</label>
                    <p className="text-sm text-gray-900">{selectedOrder.customer_id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Order Amount</label>
                    <p className="text-sm text-gray-900 font-medium">₹{selectedOrder.order_amount}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      selectedOrder.status === 'Completed' || selectedOrder.status === 'SUCCESS'
                        ? 'bg-green-100 text-green-800'
                        : selectedOrder.status === 'INITIATED' || selectedOrder.status === 'PENDING'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedOrder.status}
                    </span>
                  </div>
                </div>

                {/* Customer 1 Details */}
                <div className="border-t pt-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Customer 1 Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Name</label>
                      <p className="text-sm text-gray-900">{selectedOrder.customer1_name}</p>
                    </div>
                    {selectedOrder.customer1_gender && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Gender</label>
                        <p className="text-sm text-gray-900 capitalize">{selectedOrder.customer1_gender}</p>
                      </div>
                    )}
                    {selectedOrder.customer1_dob && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                        <p className="text-sm text-gray-900">
                          {format(new Date(selectedOrder.customer1_dob.replace('Z', '')), 'MMM dd, yyyy')}
                        </p>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">City</label>
                      <p className="text-sm text-gray-900">{selectedOrder.customer1_city}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">State</label>
                      <p className="text-sm text-gray-900">{selectedOrder.customer1_state}</p>
                    </div>
                  </div>
                </div>

                {/* Customer 2 Details */}
                {selectedOrder.customer2_name && (
                  <div className="border-t pt-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Customer 2 Details</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Name</label>
                        <p className="text-sm text-gray-900">{selectedOrder.customer2_name}</p>
                      </div>
                      {selectedOrder.customer2_gender && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Gender</label>
                          <p className="text-sm text-gray-900 capitalize">{selectedOrder.customer2_gender}</p>
                        </div>
                      )}
                      {selectedOrder.customer2_dob && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                          <p className="text-sm text-gray-900">
                            {format(new Date(selectedOrder.customer2_dob.replace('Z', '')), 'MMM dd, yyyy')}
                          </p>
                        </div>
                      )}
                      {selectedOrder.customer2_city && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">City</label>
                          <p className="text-sm text-gray-900">{selectedOrder.customer2_city}</p>
                        </div>
                      )}
                      {selectedOrder.customer2_state && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">State</label>
                          <p className="text-sm text-gray-900">{selectedOrder.customer2_state}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Contact Information */}
                <div className="border-t pt-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Contact Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <p className="text-sm text-gray-900">{selectedOrder.customer_email}</p>
                    </div>
                    {selectedOrder.customer_phone && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Phone</label>
                        <p className="text-sm text-gray-900">{selectedOrder.customer_phone}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Additional Information */}
                <div className="border-t pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Reading Type</label>
                      <p className="text-sm text-gray-900">{selectedOrder.reading_type}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Created At</label>
                      <p className="text-sm text-gray-900">
                        {format(new Date(selectedOrder.created_at.replace('Z', '')), 'MMM dd, yyyy, hh:mm a')}
                      </p>
                    </div>
                    {selectedOrder.payment_status && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Payment Status</label>
                        <p className="text-sm text-gray-900">{selectedOrder.payment_status}</p>
                      </div>
                    )}
                  </div>
                </div>

                {paymentStatus && (
                  <div className="border-t pt-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Payment Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Payment Status</label>
                        <p className="text-sm text-gray-900">{paymentStatus.status}</p>
                      </div>
                      {paymentStatus.amount && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Amount</label>
                          <p className="text-sm text-gray-900">₹{paymentStatus.amount}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowModal(false)}
                  className="btn-outline"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingOrder && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Compatibility Order</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Customer 1 Name</label>
                  <input
                    type="text"
                    value={editingOrder.customer1_name}
                    onChange={(e) => setEditingOrder({...editingOrder, customer1_name: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    value={editingOrder.customer_email}
                    onChange={(e) => setEditingOrder({...editingOrder, customer_email: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <input
                    type="text"
                    value={editingOrder.customer_phone || ''}
                    onChange={(e) => setEditingOrder({...editingOrder, customer_phone: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <select
                    value={editingOrder.status}
                    onChange={(e) => setEditingOrder({...editingOrder, status: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="INITIATED">INITIATED</option>
                    <option value="PENDING">PENDING</option>
                    <option value="SUCCESS">SUCCESS</option>
                    <option value="FAILED">FAILED</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={(page) => {
            const newOffset = (page - 1) * filters.limit;
            setFilters(prev => ({ ...prev, offset: newOffset }));
          }}
          onItemsPerPageChange={(newLimit) => {
            setFilters(prev => ({ ...prev, limit: newLimit, offset: 0 }));
          }}
        />
      )}
    </div>
  );
};

export default LoveCompatibility;
